import { useRoute, Link } from "wouter";
import { SmartLayout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { useGetGymPublic, useGetStudentGyms, useAddStudentGym } from "@workspace/api-client-react";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { useState } from "react";
import {
  MapPin, Users, Star, Clock, CreditCard, Package,
  ArrowRight, Building2, Calendar, CheckCircle2, Loader2
} from "lucide-react";

export default function GymProfile() {
  const [, params] = useRoute("/gym/:slug");
  const slug = params?.slug || "";
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [isJoining, setIsJoining] = useState(false);

  const { data, isLoading, error } = useGetGymPublic(slug, {
    query: { enabled: !!slug },
  });

  const isStudent = isAuthenticated && user?.role === "STUDENT";

  const { data: studentGyms, isLoading: gymsLoading, refetch: refetchGyms } = useGetStudentGyms({
    query: { enabled: isStudent },
  });
  const addGymMutation = useAddStudentGym();

  const gymId = data?.gym?.id;
  const gymsResolved = !isStudent || (!gymsLoading && studentGyms !== undefined);
  const isAlreadyMember = isStudent && studentGyms
    ? studentGyms.some((sg: { gymId: string }) => sg.gymId === gymId)
    : false;

  const handleJoinGym = async () => {
    if (!gymId) return;
    setIsJoining(true);
    try {
      await addGymMutation.mutateAsync({ data: { gymId } });
      toast({ title: "Gym joined!", description: `You've been added to ${data?.gym?.name}.` });
      refetchGyms();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "Something went wrong";
      toast({ title: "Error", description: msg, variant: "destructive" });
    } finally {
      setIsJoining(false);
    }
  };

  if (isLoading) {
    return (
      <SmartLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </SmartLayout>
    );
  }

  if (error || !data) {
    return (
      <SmartLayout>
        <SEOHead
          title="Gym Not Found"
          description="This gym doesn't exist or may have been removed."
          canonicalPath={`/gym/${slug}`}
          noindex
        />
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
          <Building2 className="w-12 h-12 text-white/20" />
          <h1 className="text-2xl font-display font-bold text-white">Gym not found</h1>
          <p className="text-white/50">This gym doesn't exist or may have been removed.</p>
          <Link href="/explore/gyms">
            <Button variant="outline" className="border-white/10 mt-4">Browse All Gyms</Button>
          </Link>
        </div>
      </SmartLayout>
    );
  }

  const { gym, trainers, locations, packages, membershipPlans } = data;

  return (
    <SmartLayout>
      <SEOHead
        title={`${gym.name} — Martial Arts Gym on Open Mat`}
        description={`Book private lessons at ${gym.name}${gym.address ? ` in ${gym.address}` : ""}. View trainers, session packages, and membership plans. ${trainers.length} trainer${trainers.length !== 1 ? "s" : ""} available.`}
        canonicalPath={`/gym/${slug}`}
      />

      <div className="py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="flex flex-col md:flex-row md:items-center gap-6 mb-8">
              <div className="w-20 h-20 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 overflow-hidden">
                {gym.logoUrl ? (
                  <img src={gym.logoUrl} alt={`${gym.name} logo`} className="w-full h-full object-cover rounded-2xl" loading="lazy" />
                ) : (
                  <Building2 className="w-8 h-8 text-primary" />
                )}
              </div>
              <div className="flex-1">
                <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-2">
                  {gym.name}
                </h1>
                {gym.address && (
                  <p className="text-white/40 flex items-center gap-2 text-lg">
                    <MapPin className="w-5 h-5" />
                    {gym.address}
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                {!isAuthenticated && (
                  <Link href="/login">
                    <Button variant="outline" className="border-white/10 h-12 px-6 rounded-xl">
                      Log In
                    </Button>
                  </Link>
                )}
                {isStudent ? (
                  !gymsResolved ? (
                    <Button
                      disabled
                      className="bg-primary/50 text-primary-foreground font-bold h-12 px-6 rounded-xl gap-2"
                    >
                      <Loader2 className="w-4 h-4 animate-spin" />
                    </Button>
                  ) : isAlreadyMember ? (
                    <Button
                      disabled
                      className="bg-white/10 text-white/60 font-bold h-12 px-6 rounded-xl cursor-default gap-2"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      Your Gym
                    </Button>
                  ) : (
                    <Button
                      onClick={handleJoinGym}
                      disabled={isJoining}
                      className="bg-primary text-primary-foreground font-bold h-12 px-6 rounded-xl shadow-lg shadow-primary/20 gap-2"
                    >
                      {isJoining && <Loader2 className="w-4 h-4 animate-spin" />}
                      {isJoining ? "Joining..." : "Join This Gym"}
                    </Button>
                  )
                ) : !isAuthenticated ? (
                  <Link href={`/register?gym=${encodeURIComponent(slug)}`}>
                    <Button className="bg-primary text-primary-foreground font-bold h-12 px-6 rounded-xl shadow-lg shadow-primary/20">
                      Join This Gym
                    </Button>
                  </Link>
                ) : null}
              </div>
            </div>
          </motion.div>

          {trainers.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-display font-bold text-white mb-6 flex items-center gap-2">
                <Users className="w-6 h-6 text-primary" />
                Trainers
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {trainers.map((trainer) => (
                  <div
                    key={trainer.id}
                    className="group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 hover:bg-white/[0.04] transition-all duration-300 hover:-translate-y-1"
                  >
                    <div className="flex items-start gap-4 mb-3">
                      <div className="w-12 h-12 rounded-xl bg-secondary flex items-center justify-center text-lg font-bold text-white shrink-0 overflow-hidden">
                        {trainer.avatarUrl ? (
                          <img src={trainer.avatarUrl} alt={`${trainer.firstName} ${trainer.lastName} profile photo`} className="w-full h-full object-cover" loading="lazy" />
                        ) : (
                          trainer.firstName[0]
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-white">
                          {trainer.firstName} {trainer.lastName}
                        </h3>
                        <div className="flex items-center gap-2 mt-0.5">
                          {trainer.avgRating != null && (
                            <span className="flex items-center gap-1 text-sm text-primary">
                              <Star className="w-3.5 h-3.5 fill-primary" />
                              {Number(trainer.avgRating).toFixed(1)}
                            </span>
                          )}
                          <span className="text-xs text-white/40">
                            {trainer.totalSessions} sessions
                          </span>
                        </div>
                      </div>
                    </div>
                    {trainer.specialties && trainer.specialties.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {(trainer.specialties as string[]).slice(0, 3).map((s) => (
                          <span key={s} className="px-2 py-0.5 bg-white/[0.04] rounded-lg text-xs text-white/50 border border-white/[0.08]">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-3 border-t border-white/[0.06]">
                      {trainer.hourlyRate != null && (
                        <span className="text-lg font-mono text-primary font-bold">
                          ${trainer.hourlyRate}/hr
                        </span>
                      )}
                      {trainer.publicSlug ? (
                        <Link href={`/trainer/${trainer.publicSlug}`}>
                          <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold rounded-xl gap-1">
                            View Profile
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Button>
                        </Link>
                      ) : (
                        <Link href={`/book?trainerId=${trainer.id}&gymId=${gym.id}`}>
                          <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold rounded-xl gap-1">
                            Book
                            <ArrowRight className="w-3.5 h-3.5" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.section>
          )}

          {locations.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-display font-bold text-white mb-6 flex items-center gap-2">
                <MapPin className="w-6 h-6 text-primary" />
                Locations
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {locations.map((loc) => (
                  <div
                    key={loc.id}
                    className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6"
                  >
                    <h3 className="text-lg font-bold text-white mb-1">{loc.name}</h3>
                    <p className="text-sm text-white/40 flex items-center gap-1">
                      <MapPin className="w-3.5 h-3.5" />
                      {loc.address}
                      {loc.city && `, ${loc.city}`}
                      {loc.state && `, ${loc.state}`}
                      {loc.zipCode && ` ${loc.zipCode}`}
                    </p>
                  </div>
                ))}
              </div>
            </motion.section>
          )}

          {packages.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-display font-bold text-white mb-6 flex items-center gap-2">
                <Package className="w-6 h-6 text-primary" />
                Session Packages
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {packages.map((pkg) => (
                  <div
                    key={pkg.id}
                    className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6"
                  >
                    <h3 className="text-lg font-bold text-white mb-1">{pkg.name}</h3>
                    <p className="text-3xl font-mono font-bold text-primary mb-2">
                      ${pkg.priceTotal}
                    </p>
                    <p className="text-sm text-white/40">
                      {pkg.sessionCount} session{pkg.sessionCount !== 1 ? "s" : ""}
                      {pkg.expiryDays && ` - Valid for ${pkg.expiryDays} days`}
                    </p>
                  </div>
                ))}
              </div>
            </motion.section>
          )}

          {membershipPlans.length > 0 && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mb-12"
            >
              <h2 className="text-2xl font-display font-bold text-white mb-6 flex items-center gap-2">
                <CreditCard className="w-6 h-6 text-primary" />
                Membership Plans
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {membershipPlans.map((plan) => (
                  <div
                    key={plan.id}
                    className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6"
                  >
                    <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>
                    <p className="text-3xl font-mono font-bold text-primary mb-2">
                      {plan.discountPct > 0 ? (
                        <>
                          <span className="text-lg text-white/30 line-through">${plan.priceMonthly}</span>{" "}
                          ${(plan.priceMonthly * (1 - plan.discountPct / 100)).toFixed(2)}
                        </>
                      ) : (
                        <>${plan.priceMonthly}</>
                      )}
                      <span className="text-sm text-white/40 font-sans font-normal">/mo</span>
                    </p>
                    {plan.discountPct > 0 && (
                      <p className="text-xs text-emerald-400 font-medium mb-1">{plan.discountPct}% discount</p>
                    )}
                    {plan.privateCredits != null && (
                      <p className="text-sm text-white/40">
                        {plan.privateCredits === 0 ? "No" : plan.privateCredits} private session{plan.privateCredits !== 1 ? "s" : ""} included
                      </p>
                    )}


                  </div>
                ))}
              </div>
            </motion.section>
          )}
        </div>
      </div>
    </SmartLayout>
  );
}
